import PageHeading from "@components/zenith/ui/PageHeading";
import { useAppContext } from "@context/AppContext";
import {
  ArrowUpRightIcon,
  ArrowsRightLeftIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { useGetAllWorkflowGroups } from "@hooks/query/useGetAllWorkflowGroups";
import { FunctionComponent, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

/**
 * The wizards the portal has, each with its tile's icon, by workflow
 * identifier: each names its route under /zenith/workflows. Which tiles show,
 * and under which group, is the server's: a tile needs a /workflowGroups row
 * the user holds the permissions for.
 */
const WIZARDS: Record<string, typeof PencilSquareIcon> = {
  water_declaration: PencilSquareIcon,
  create_forward_draw: ArrowUpRightIcon,
  create_admin_transfer: ArrowsRightLeftIcon,
};

const WorkflowIndex: FunctionComponent = () => {
  const { t } = useTranslation();
  const { setInfoPanel, checkPermissions } = useAppContext();
  const { data: workflowGroups = [] } = useGetAllWorkflowGroups();

  const groups = workflowGroups
    .map((group: any) => ({
      ...group,
      workflows: group.workflows.filter(
        (workflow: any) =>
          WIZARDS[workflow.identifier] &&
          checkPermissions(workflow.permissions.map((p: any) => p.name)),
      ),
    }))
    .filter((group: any) => group.workflows.length);

  useEffect(() => {
    setInfoPanel(
      <div className="space-y-1 leading-7 text-gray-500 text-sm flex flex-col gap-4">
        <p>{t("zenith.workflows.info1")}</p>
        <p>{t("zenith.workflows.info2")}</p>
        <p>{t("zenith.workflows.info3")}</p>
      </div>,
    );
  }, [setInfoPanel, t]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeading
        title={t("zenith.navigation.workflows")}
        sub={t("zenith.workflows.subtitle")}
      />
      {groups.map((group: any) => (
        <WorkflowGroupTiles key={group.identifier} group={group} />
      ))}
    </div>
  );
};

const WorkflowGroupTiles: FunctionComponent<{ group: any }> = ({ group }) => {
  const { t } = useTranslation();
  const headingId = useId();

  return (
    <section aria-labelledby={headingId}>
      <h2
        id={headingId}
        className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500"
      >
        {t(`zenith.workflows.workflow_groups.${group.identifier}`, {
          defaultValue: group.description,
        })}
      </h2>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {group.workflows.map(({ identifier }: any) => {
          const Icon = WIZARDS[identifier];
          return (
            <li key={identifier}>
              <Link
                to={identifier}
                className="flex h-full flex-col gap-2 rounded-lg border border-gray-200 bg-white px-5 py-4 transition hover:border-primary-1 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-1"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-1/10 text-primary-1">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="text-sm font-bold text-primary-3">
                  {t(`zenith.workflows.${identifier}`)}
                </span>
                <span className="text-xs leading-5 text-gray-500">
                  {t(`zenith.workflows.descriptions.${identifier}`)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default WorkflowIndex;
